import { z } from "zod";

export const VIDEO_INTERVIEW_STATUSES = [
  "created",
  "link_issued",
  "opened",
  "consent_received",
  "recording",
  "processing",
  "completed",
  "failed",
  "cancelled",
  "video_deleted",
] as const;

export type VideoInterviewStatus = (typeof VIDEO_INTERVIEW_STATUSES)[number];

export const VIDEO_INTERVIEW_STATUS_LABELS: Record<
  VideoInterviewStatus,
  string
> = {
  created: "Создано",
  link_issued: "Ссылка выдана",
  opened: "Ссылка открыта",
  consent_received: "Согласие получено",
  recording: "Идёт запись",
  processing: "Обрабатывается",
  completed: "Завершено",
  failed: "Ошибка",
  cancelled: "Отменено",
  video_deleted: "Видео удалено",
};

/**
 * Сколько дней живёт ссылка кандидата. Срок задаёт система, а не рекрутёр:
 * человеку всегда хочется побольше, а каждый лишний день ссылки — лишний день,
 * когда её могут открыть не тем человеком. Потерялась или истекла — рекрутёр
 * выдаёт новую, и прежняя в ту же секунду гаснет.
 */
export const CANDIDATE_LINK_DAYS = 7;

/**
 * Статусы, при которых кандидату ещё можно выдать ссылку заново. Встреча жива,
 * и человеку есть куда войти.
 *
 * `recording` здесь намеренно: у кандидата упала вкладка посреди записи — это
 * ровно тот случай, когда ссылка нужна сию минуту, а не «в следующий раз».
 * После `processing` выдавать уже нечего: разговор кончился, дорожки уехали на
 * сервер, и вход в комнату ничего не изменит.
 */
export const VIDEO_INTERVIEW_REISSUABLE_STATUSES = [
  "created",
  "link_issued",
  "opened",
  "consent_received",
  "recording",
] as const satisfies readonly VideoInterviewStatus[];

/** Одно правило для сервера и для кнопки в истории интервью. */
export function canReissueCandidateLink(status: VideoInterviewStatus): boolean {
  return (VIDEO_INTERVIEW_REISSUABLE_STATUSES as readonly string[]).includes(
    status
  );
}

/**
 * Статусы, при которых встреча уже состоялась: разговор кончился, дорожки
 * ушли на сервер, и в комнату входить незачем. Кандидату по ссылке при них
 * показывают «Интервью уже прошло» — единственное оставшееся действие: отзыв
 * согласия (право по ст. 8 закона РК № 94-V). `cancelled` сюда не входит —
 * это «отменено», и говорится об этом другими словами.
 */
export const VIDEO_INTERVIEW_FINISHED_STATUSES = [
  "processing",
  "completed",
  "failed",
  "video_deleted",
] as const satisfies readonly VideoInterviewStatus[];

/** Одно правило для сервера (joinCandidateRoom) и для страницы кандидата. */
export function isVideoInterviewFinished(status: VideoInterviewStatus): boolean {
  return (VIDEO_INTERVIEW_FINISHED_STATUSES as readonly string[]).includes(
    status
  );
}

/**
 * Тексты отказов комнаты — ровно те слова, которые видит человек. Сервер
 * кладёт их в TRPCError, клиент показывает как есть (контракт ночной стройки
 * 19.09.2026, пункты 1, 3, 4). Держатся здесь, чтобы обе стороны сверялись с
 * одной строкой, а не с копией.
 */
export const VIDEO_INTERVIEW_ROOM_MESSAGES = {
  /** Ссылка кандидата одноразовая: другой браузер по ней не входит. */
  foreignDevice:
    "Ссылка уже использована на другом устройстве. Попросите рекрутёра выслать её заново.",
  /** Встреча состоялась — в комнату больше не пускают. */
  finished: "Интервью уже прошло",
  /** Отменено (в том числе отзывом согласия) или запись удалена по сроку. */
  closed: "Интервью отменено или больше недоступно",
  /** Слишком частые запросы с одного адреса или по одному пропуску. */
  tooManyRequests: "Слишком много запросов, подождите минуту",
  /** Материалы отменённого интервью сотруднику не выдаются. */
  materialsUnavailable: "Интервью отменено, материалы недоступны",
} as const;

/** Роль рекрутёра занята другим сотрудником — отказ, не вытеснение. */
export function recruiterRoomOccupiedMessage(occupantName: string): string {
  return `В комнате уже есть рекрутёр: ${occupantName}. Переназначить интервью можно на карточке кандидата.`;
}

/**
 * Решение кандидата по согласию. Оно ограничивает запись, а не саму встречу:
 * комната открывается при любом значении, включая `null` (решения ещё нет).
 */
export type VideoInterviewConsentDecision = "accepted" | "declined";

export const videoInterviewIdSchema = z.object({
  interviewId: z.number().int().positive(),
});

export const videoInterviewCreateSchema = z.object({
  candidateId: z.number().int().positive(),
  vacancyId: z.number().int().positive(),
});

/**
 * Переназначить рекрутёра интервью: меняется только интервью, не вакансия.
 * Кому можно — тому же, кто вправе войти в комнату рекрутёром.
 */
export const videoInterviewReassignSchema = z.object({
  interviewId: z.number().int().positive(),
  recruiterId: z.number().int().positive(),
});

export const videoInterviewTokenSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/, "Некорректная ссылка на интервью");

export const videoInterviewConsentSchema = z.object({
  token: videoInterviewTokenSchema,
  decision: z.enum(["accepted", "declined"]),
  recording: z.boolean(),
  clientTransfer: z.boolean(),
}).superRefine((value, ctx) => {
  if (value.decision === "accepted" && (!value.recording || !value.clientTransfer)) {
    ctx.addIssue({
      code: "custom",
      path: ["recording"],
      message: "Для согласия нужно подтвердить оба пункта",
    });
  }
});

/**
 * Пропуск в комнату. В одном поле два вида, и оба обязаны приниматься:
 *
 * • подписанный — base64url(json) + "." + base64url(подпись). По нему сервер
 *   узнаёт участника даже после перезапуска приложения (videoInterviewRoom.ts);
 * • прежний — 43 знака случайных байтов. Такие держат вкладки, открытые до
 *   19.09.2026. Ломать их выкаткой нельзя: именно в такой вкладке и лежит
 *   недоотправленная запись, ради которой всё и делалось.
 */
export const videoInterviewRoomSessionTokenSchema = z
  .string()
  .regex(
    /^[A-Za-z0-9_-]{43}$|^[A-Za-z0-9_-]{8,400}\.[A-Za-z0-9_-]{43}$/,
    "Некорректная сессия комнаты"
  );

const rtcDescriptionSchema = z.object({
  type: z.enum(["offer", "answer"]),
  sdp: z.string().min(1).max(250_000),
});

const rtcIceCandidateSchema = z.object({
  candidate: z.string().max(20_000).nullable().optional(),
  sdpMid: z.string().max(200).nullable().optional(),
  sdpMLineIndex: z.number().int().nullable().optional(),
  usernameFragment: z.string().max(200).nullable().optional(),
});

/**
 * Пауза записи. Приходит тем же каналом, что и сигналы соединения, но это не
 * сигнал браузеру браузера: сервер его не пересылает, а запоминает в комнате и
 * отдаёт всем в pollRoom. Иначе кандидат, у которого вкладка перезагрузилась,
 * не узнал бы о паузе вовсе — старые сообщения комнаты ему уже не приходят.
 *
 * Включает и снимает паузу только рекрутёр: он же и управляет записью.
 */
const recordingPausedSchema = z.object({ paused: z.boolean() });

export const videoInterviewRoomSignalSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("offer"), payload: rtcDescriptionSchema }),
  z.object({ type: z.literal("answer"), payload: rtcDescriptionSchema }),
  z.object({ type: z.literal("ice-candidate"), payload: rtcIceCandidateSchema }),
  z.object({
    type: z.literal("recording-paused"),
    payload: recordingPausedSchema,
  }),
  /**
   * Встреча окончена — гасите эфир.
   *
   * 19.09.2026 рекрутёр нажала «Остановить и завершить», увидела «интервью
   * завершено» и говорила дальше, не зная, что кандидат всё это время её
   * видит и слышит: камера и микрофон гасли только при закрытии вкладки.
   * Этот сигнал разрывает эфир у обоих в тот же миг, а не когда человек
   * догадается закрыть страницу.
   */
  z.object({ type: z.literal("interview-finished") }),
  /**
   * Микрофон выключен или снова включён.
   *
   * Без этого сигнала собеседник не знал, слышно его или нет: он видел
   * молчащую плитку и не мог отличить «человек задумался» от «человек говорит
   * в выключенный микрофон». В обычных звонках перечёркнутый микрофон на
   * плитке есть всегда, и люди на него смотрят (директива совета 19.09.2026).
   */
  z.object({
    type: z.literal("mic-state"),
    payload: z.object({ muted: z.boolean() }),
  }),
]);

/**
 * Опрос комнаты. Пропуск приходит либо заголовком `x-vi-session` (новые
 * вкладки: pollRoom — GET, и без заголовка пропуск оказывался бы в строке
 * запроса и в логах прокси), либо полем в теле — так его шлют вкладки,
 * открытые до этой правки. Ни того ни другого — сервер отвечает UNAUTHORIZED.
 */
export const VIDEO_INTERVIEW_SESSION_HEADER = "x-vi-session";

export const videoInterviewRoomPollSchema = z.object({
  sessionToken: videoInterviewRoomSessionTokenSchema.optional(),
  afterId: z.number().int().nonnegative().max(1_000_000),
  /**
   * Порядковый номер входа в комнату в этой вкладке. Сервер его НЕ читает —
   * он нужен только клиенту, чтобы разные сессии не делили один ответ в кэше.
   *
   * Зачем поле в контракте (аудит 21.09.2026). Пропуск убрали из входа, чтобы
   * он не уезжал в строку адреса, и ключ кэша свёлся к одному «с какого
   * события читаем». После обрыва связи кандидат перезаходит, счётчик событий
   * сбрасывается в ноль — и запрос мгновенно отдаёт снимок ПРОШЛОЙ сессии из
   * кэша. В том снимке запись ещё не шла, а комната по нему решает, что запись
   * остановлена, и закрывает дорожку кандидата посреди встречи, причём как
   * целую. Ровно та потеря, ради которой писан весь модуль. Номер входа
   * разводит сессии по разным ключам, а секретом не является: это просто
   * счётчик 1, 2, 3.
   */
  seq: z.number().int().nonnegative().max(100_000).optional(),
});

/**
 * ICE-серверы выдаются только участнику с живым пропуском: вместе с ними
 * уходит временный логин к нашему TURN, и без пропуска его получал бы любой,
 * кто знает адрес. Порядок на клиенте: камера → join → iceServers → peer.
 */
/**
 * Настройки подключения. Пропуск НЕОБЯЗАТЕЛЕН нарочно.
 *
 * Аудит 20.09.2026: сделав его обязательным, мы сломали бы все вкладки,
 * открытые до выкатки, — старая сборка зовёт этот вызов без аргументов,
 * получила бы 400, и кнопка «Проверить и войти» осталась бы серой навсегда,
 * без единого слова о том, что надо обновить страницу. Поэтому переход в два
 * шага: без пропуска отдаём только публичные STUN (ровно то, что прод отдавал
 * до двери), с пропуском — ещё и логин к нашему TURN в Астане. Обязательным
 * поле станет через две недели, когда старых вкладок не останется.
 */
export const videoInterviewIceServersInputSchema = z.object({
  sessionToken: videoInterviewRoomSessionTokenSchema.optional(),
});

export const videoInterviewRoomSignalInputSchema = z.object({
  sessionToken: videoInterviewRoomSessionTokenSchema,
  signal: videoInterviewRoomSignalSchema,
});

/**
 * На чём сорвалась запись у человека в комнате. Браузер сам сообщает об этом
 * серверу: части записи в боевом режиме уходят мимо CRM, прямо на узел, и
 * иначе о потерянной встрече никто бы не узнал до жалобы рекрутёра.
 */
export const VIDEO_INTERVIEW_FAILURE_STAGES = [
  "recording",
  "upload",
  "finish",
] as const;

export type VideoInterviewFailureStage =
  (typeof VIDEO_INTERVIEW_FAILURE_STAGES)[number];

export const VIDEO_INTERVIEW_FAILURE_STAGE_LABELS: Record<
  VideoInterviewFailureStage,
  string
> = {
  recording: "запись",
  upload: "отправка записи на сервер",
  finish: "завершение интервью",
};

export const videoInterviewFailureSchema = z.object({
  sessionToken: videoInterviewRoomSessionTokenSchema,
  stage: z.enum(VIDEO_INTERVIEW_FAILURE_STAGES),
  /** Текст ошибки, который увидел человек. Не разметка — в алерт уходит экранированным. */
  message: z.string().trim().min(1).max(500),
});

export interface VideoInterviewListItem {
  id: number;
  seriesRootId: number | null;
  partNumber: number;
  branchId: number;
  candidateId: number;
  vacancyId: number;
  vacancyTitle: string;
  companyName: string;
  recruiterId: number;
  recruiterName: string | null;
  status: VideoInterviewStatus;
  createdById: number;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  recordingStartedAt: string | null;
  recordingFinishedAt: string | null;
  videoDeleteAt: string | null;
  /** Срок хранения расшифровки и краткой оценки; null — текста нет. */
  textDeleteAt: string | null;
  /** Explicit owner deletion of all interview materials; audit evidence remains. */
  materialsDeletedAt?: string | null;
  /**
   * Рекрутёр попросил удалить запись, решение за владелицей. null — запроса
   * нет (решение 21.09.2026: удаление необратимо, поэтому в два шага).
   */
  deletionRequestedAt: string | null;
  deletionReason: string | null;
  deletionRequestedByName: string | null;
  activeLinkExpiresAt: string | null;
}

export interface VideoInterviewAuditItem {
  id: number;
  eventType: string;
  actorType: "user" | "candidate" | "client" | "system";
  actorId: number | null;
  actorName: string | null;
  createdAt: string;
}

export type VideoInterviewRoomSignal = z.infer<
  typeof videoInterviewRoomSignalSchema
>;

export const VIDEO_INTERVIEW_ASSET_KINDS = [
  "composite_video",
  "candidate_audio",
  "recruiter_audio",
] as const;

export type VideoInterviewAssetKind =
  (typeof VIDEO_INTERVIEW_ASSET_KINDS)[number];

export const videoInterviewAssetKindSchema = z.enum(
  VIDEO_INTERVIEW_ASSET_KINDS
);

export const videoInterviewAssetBeginSchema = z.object({
  sessionToken: videoInterviewRoomSessionTokenSchema,
  kind: videoInterviewAssetKindSchema,
  mimeType: z.string().min(1).max(128),
  /**
   * Номер интервью. Нужен запасному пути приёма записи: пропуск не признан
   * (просрочен — встреча длиннее двух часов — или испорчен), и право
   * прислать дорожку остаётся только по учётной записи сотрудника — тогда
   * интервью надо назвать. Необязателен ради вкладок, открытых до этой
   * правки: они его не шлют, и запасной путь им недоступен.
   */
  interviewId: z.number().int().positive().optional(),
});

/**
 * Что разрешено писать в каждую дорожку. Одно правило на оба пути загрузки —
 * локальный каталог (videoInterviewStorage) и узел (videoNodeClient), — и то
 * же самое отдельно повторено у видеоузла: кода CRM он не импортирует.
 *
 * Список шире одного webm, потому что айфон писать webm не умеет: Safari
 * отдаёт mp4, а часть сборок WebKit — сырой поток AAC. Что именно выбрать,
 * решает браузер через MediaRecorder.isTypeSupported, а не наш список
 * «что умеет iOS»: такой список устаревает молча.
 */
export const VIDEO_INTERVIEW_ASSET_MIMES: Record<
  VideoInterviewAssetKind,
  readonly string[]
> = {
  composite_video: ["video/webm", "video/mp4"],
  candidate_audio: ["audio/webm", "audio/mp4", "audio/aac"],
  recruiter_audio: ["audio/webm", "audio/mp4", "audio/aac"],
};

/** Расширение файла по типу содержимого: у каждого разрешённого типа своё. */
export const VIDEO_INTERVIEW_MIME_EXTENSIONS: Record<string, string> = {
  "video/webm": "webm",
  "video/mp4": "mp4",
  "audio/webm": "webm",
  "audio/mp4": "mp4",
  "audio/aac": "aac",
};

/** Семейство типа без параметров: «video/webm;codecs=vp8,opus» → «video/webm». */
export function videoInterviewMimeFamily(mimeType: string): string {
  return mimeType.split(";", 1)[0].trim().toLowerCase();
}

/** Можно ли писать такой тип содержимого в эту дорожку. Параметр codecs не участвует. */
export function videoInterviewAssetMimeAllowed(
  kind: VideoInterviewAssetKind,
  mimeType: string
): boolean {
  return VIDEO_INTERVIEW_ASSET_MIMES[kind].includes(
    videoInterviewMimeFamily(mimeType)
  );
}

/** Бросает понятную ошибку, если тип содержимого не подходит дорожке. */
export function assertVideoInterviewAssetMime(
  kind: VideoInterviewAssetKind,
  mimeType: string
): void {
  if (!videoInterviewAssetMimeAllowed(kind, mimeType)) {
    throw new Error(
      kind === "composite_video"
        ? "Для объединённого видео разрешены только видеозаписи"
        : "Для отдельных дорожек разрешён только звук"
    );
  }
}

/**
 * Под каким расширением дорожка ляжет в локальном каталоге. Зашивать «.webm»
 * нельзя: с айфона приходит mp4, и файл с чужим расширением потом не сыграет
 * у заказчика и запутает уборку хранилища.
 */
export function videoInterviewAssetExtension(mimeType: string): string | null {
  return (
    VIDEO_INTERVIEW_MIME_EXTENSIONS[videoInterviewMimeFamily(mimeType)] ?? null
  );
}

/**
 * Под каким типом содержимого запись уходит заказчику. Берём из расширения
 * файла, а не из строки базы: на диске лежит то, что записал браузер, и с
 * айфона это mp4. Зашитый рядом с `nosniff` «video/webm» не дал бы плееру даже
 * догадаться о настоящем формате — вышло бы «Не удалось загрузить запись» на
 * любом устройстве. Ровно так же считает тип отдача видеоузла (mimeForFile).
 *
 * Расширение говорит только о контейнере, звук это или видео — знает тип из
 * базы. Расширение чужое (строка старого формата) — отдаём тип из базы, если
 * он из нашего списка; иначе объединённое видео, единственное, что тут течёт.
 */
export function videoInterviewPlaybackContentType(
  storageKey: string,
  mimeType: string
): string {
  const family = videoInterviewMimeFamily(mimeType);
  const dot = storageKey.lastIndexOf(".");
  const extension = dot < 0 ? "" : storageKey.slice(dot + 1).toLowerCase();
  if (extension === "aac") return "audio/aac";
  if (extension === "webm" || extension === "mp4") {
    return `${family.startsWith("audio/") ? "audio" : "video"}/${extension}`;
  }
  return family in VIDEO_INTERVIEW_MIME_EXTENSIONS ? family : "video/webm";
}

export const videoInterviewAssetFinalizeSchema = z.object({
  sessionToken: videoInterviewRoomSessionTokenSchema,
  /** Зачем он здесь — см. videoInterviewAssetBeginSchema. */
  interviewId: z.number().int().positive().optional(),
  uploadId: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  kind: videoInterviewAssetKindSchema,
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  /**
   * Запись оборвалась: закрывают не всю дорожку, а то, что успело доехать до
   * сервера, — вкладка умерла посреди встречи либо связь пропала так надолго,
   * что копить стало нечем. Такая дорожка помечается в журнале интервью и не
   * выдаётся за целую.
   */
  partial: z.boolean().optional(),
});

/**
 * Ответ assetBegin. `nodeUrl`/`uploadPass` заполнены только в режиме
 * видеоузла: тогда части записи уходят с браузера прямо на узел в Астане, а
 * CRM остаётся тем, кто проверяет право на запись и выдаёт пропуск. Без узла
 * оба поля `null`, и клиент шлёт части прежним адресом.
 */
export interface VideoInterviewAssetBeginResult {
  uploadId: string;
  maxChunkBytes: number;
  nodeUrl: string | null;
  uploadPass: string | null;
}

export interface VideoInterviewAssetItem {
  id: number;
  interviewId: number;
  kind: VideoInterviewAssetKind;
  mimeType: string;
  byteSize: number;
  sha256: string | null;
  status: "uploading" | "ready" | "deleted" | "failed";
  /**
   * Дорожку закрыли обрывком: встреча писалась дальше, а на сервер попало
   * только начало. Файл настоящий и открывается, но он короче разговора —
   * поэтому запись помечена, а не выдана за целую.
   */
  partial: boolean;
  deleteAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const VIDEO_INTERVIEW_ROOM_ROLES = ["candidate", "recruiter"] as const;

export type VideoInterviewRoomRole = (typeof VIDEO_INTERVIEW_ROOM_ROLES)[number];

/**
 * Кто имеет право загружать дорожку: кандидат — только свой звук, рекрутёр —
 * объединённое видео и свой звук. Одна карта для tRPC (assetBegin /
 * assetFinalize) и HTTP-обработчика чанков, чтобы правила не разъехались.
 */
export const VIDEO_INTERVIEW_ASSET_KIND_ROLES: Record<
  VideoInterviewAssetKind,
  VideoInterviewRoomRole
> = {
  composite_video: "recruiter",
  candidate_audio: "candidate",
  recruiter_audio: "recruiter",
};

const iceUrlSchema = z
  .string()
  .min(1)
  .max(512)
  .regex(
    /^(stun|stuns|turn|turns):/i,
    "Адрес ICE-сервера должен начинаться со stun: или turn:"
  );

/** Один RTCIceServer из VIDEO_INTERVIEW_ICE_SERVERS; структурно совместим с типом браузера. */
export const videoInterviewIceServerSchema = z.object({
  urls: z.union([iceUrlSchema, z.array(iceUrlSchema).min(1).max(8)]),
  username: z.string().max(512).optional(),
  credential: z.string().max(512).optional(),
});

export const videoInterviewIceServersSchema = z
  .array(videoInterviewIceServerSchema)
  .max(16);

export type VideoInterviewIceServer = z.infer<
  typeof videoInterviewIceServerSchema
>;

export interface VideoInterviewReadiness {
  consentConfigured: boolean;
  storageConfigured: boolean;
  /** Задан ли свой секрет для отпечатка IP (в доказательствах согласия). */
  ipHashSecretConfigured: boolean;
}

/**
 * Адрес записи для сотрудника агентства: собственная встреча, а не выдача
 * заказчику. Живёт минуты — это подписанный пропуск на видеоузел, а не ссылка,
 * которую куда-то сохраняют. Истёк — сотрудник нажимает «Смотреть» ещё раз.
 */
export interface VideoInterviewStaffPlayback {
  url: string;
  /** Когда адрес перестанет работать. */
  expiresAt: string;
  /** Тип содержимого для плеера: с айфона запись приходит mp4, не webm. */
  contentType: string;
  byteSize: number;
  /** Когда встречу записали; null — запись ещё не начиналась. */
  recordedAt: string | null;
}

/**
 * Новая ссылка кандидату взамен потерянной. `revokedPrevious` — сколько
 * прежних ссылок погасили: если рекрутёр всё-таки найдёт старую в переписке,
 * она уже не откроется, и лучше узнать об этом сразу.
 */
export interface VideoInterviewCandidateLinkIssued {
  token: string;
  expiresAt: string;
  revokedPrevious: number;
}
